// Ported from mobile src/utils/brainerAnalysisTransform.ts (import paths only).
import type { AnalysisResultItem, Trade } from 'types/analysis';
import type { TradeIdea, TradeMetricItem } from 'data/analysisDisplay';

// ─── Formatting helpers ───────────────────────────────────────────────────────

function formatPrice(value: number): string {
  return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formatEntry(low: number, high: number): string {
  if (low === high) return formatPrice(low);
  return `${formatPrice(low)}–${formatPrice(high)}`;
}

// ─── Bias ─────────────────────────────────────────────────────────────────────

export interface BiasDisplay {
  timeframe: string;
  pair: string;
  direction: string;
  sub: string;
  contextLines: string[];
  contextAlert: string;
  summary: string;
}

/**
 * Resolve directional bias.
 *
 * `bottom_line.bias` is free-form prose (per the backend StyleJson schema), so
 * substring-matching it for "bull" is unreliable — e.g. "the trend still favors
 * buyers" is bullish but has no "bull" token and was mislabeled Bearish.
 *
 * The Enforcer sets `invalidation.direction` deterministically from the trend:
 * "below" => bullish thesis (support break kills it), "above" => bearish.
 */
export function resolveBiasIsBullish(result: AnalysisResultItem): boolean {
  const inv = result.response.bottom_line.invalidation;
  if (inv?.direction === 'below') return true;
  if (inv?.direction === 'above') return false;
  return result.response.bottom_line.bias.toLowerCase().includes('bull'); // defensive fallback
}

export function transformBias(result: AnalysisResultItem): BiasDisplay {
  const r = result.response;
  const isBullish = resolveBiasIsBullish(result);
  const direction = isBullish ? 'Bullish' : 'Bearish';
  const sub = r.bottom_line.key_condition;

  const contextLines = [
    r.key_context.bull_case.split('.')[0].trim(),
    r.key_context.bear_case.split('.')[0].trim(),
  ];

  const invalidation = r.bottom_line.invalidation;
  const contextAlert = `Fails if: ${formatPrice(invalidation.level_price)} ${invalidation.direction}`;

  return {
    timeframe: r.timeframe.toUpperCase(),
    pair: r.asset,
    direction,
    sub,
    contextLines,
    contextAlert,
    summary: r.bottom_line.bias,
  };
}

// ─── Trade Ideas ──────────────────────────────────────────────────────────────

// Must match the backend floor (rr_validator.RR_FLOOR): targets below it only
// appear on non-tradeable "Wait" cards, and their R:R is flagged in red.
const RR_FLOOR = 1.5;

// A null R:R means the backend could not verify it (rr_validator._blank_rr) —
// unknown, not zero. `null < RR_FLOOR` is true in JS, so these must be tested
// explicitly or an unverified ratio silently reads as a sub-floor one.
const rrUnverified = (rr: number | null | undefined): boolean => rr == null;
const rrSubFloor = (rr: number | null | undefined): boolean => rr != null && rr < RR_FLOOR;
const rrText = (rr: number | null | undefined): string => (rr == null ? '—' : `1:${rr}`);

function tradeToTradeIdea(trade: Trade): TradeIdea {
  const isLong = trade.direction === 'LONG';
  const riskLabel = trade.risk_label;
  const badgeVariant: 'best' | 'decent' = riskLabel.toLowerCase() === 'best' ? 'best' : 'decent';

  const metrics: TradeMetricItem[] = [];

  metrics.push({
    label: 'Entry',
    value: formatEntry(trade.entry_low, trade.entry_high),
    note: trade.entry_label,
  });
  metrics.push({
    label: 'Stop Loss',
    value: formatPrice(trade.stop_loss),
    color: '#FF5A5A',
    note: trade.stop_label,
  });

  // A sub-floor R:R (only possible on "Wait" trades — the backend rr_validator
  // drops sub-1.5 targets from tradeable trades) renders red with an explicit note.
  const targets = trade.targets ?? [];
  if (targets.length === 1) {
    const t = targets[0];
    const subFloor = rrSubFloor(t.rr);
    const unverified = rrUnverified(t.rr);
    metrics.push({ label: t.label, value: formatPrice(t.price), color: '#00C2D4' });
    metrics.push({
      label: `R:R ${t.label}`,
      value: rrText(t.rr),
      color: subFloor || unverified ? '#FF5A5A' : '#A855F7',
      note: subFloor ? 'below 1:1.5 floor' : unverified ? 'unverified' : undefined,
    });
  } else {
    targets.forEach((t) => {
      const subFloor = rrSubFloor(t.rr);
      const unverified = rrUnverified(t.rr);
      metrics.push({
        label: t.label,
        value: formatPrice(t.price),
        color: '#00C2D4',
        fullWidth: true,
        extraValue: `R:R ${rrText(t.rr)}`,
        extraColor: subFloor || unverified ? '#FF5A5A' : '#A855F7',
        extraNote: subFloor
          ? 'below 1:1.5 floor'
          : unverified
            ? 'unverified'
            : t.condition && t.condition.length < 40
              ? t.condition
              : undefined,
      });
    });
  }

  const entryMid =
    trade.entry_low === trade.entry_high ? trade.entry_low : (trade.entry_low + trade.entry_high) / 2;

  return {
    type: trade.direction,
    dirLabel: `${isLong ? 'Long' : 'Short'} · ${riskLabel}`,
    badge: riskLabel,
    badgeVariant,
    metrics,
    executeLabel: `Execute ${isLong ? 'Long' : 'Short'} · ${riskLabel}`,
    executeData: {
      side: trade.direction,
      entry: entryMid,
      stopLoss: trade.stop_loss,
      tp1: trade.targets[0]?.price ?? null,
      tp2: trade.targets[1]?.price ?? null,
      tp1Rr: trade.targets[0]?.rr ?? null,
      tp2Rr: trade.targets[1]?.rr ?? null,
    },
  };
}

export function transformTradeIdeas(result: AnalysisResultItem): TradeIdea[] {
  return (result.response.trades ?? []).map(tradeToTradeIdea);
}

// ─── Warnings ─────────────────────────────────────────────────────────────────

export interface WarningItem {
  text: string;
  isHigh: boolean;
}

export function transformWarnings(result: AnalysisResultItem): WarningItem[] {
  const warnings: WarningItem[] = [];

  const note = result.response.current_price_note;
  if (note) {
    warnings.push({ text: note, isHigh: true });
  }

  (result.response.suggestions ?? []).forEach((s) => {
    warnings.push({ text: s.text, isHigh: false });
  });

  return warnings;
}

// ─── Invalidation ─────────────────────────────────────────────────────────────

export interface InvalidationDisplay {
  label: string;
  price: string;
  note: string;
}

export function transformInvalidation(result: AnalysisResultItem): InvalidationDisplay {
  const inv = result.response.bottom_line.invalidation;
  const isBullish = resolveBiasIsBullish(result);

  return {
    label: isBullish ? 'Bullish thesis fails if' : 'Bearish thesis fails if',
    price: `Close ${inv.direction} ${formatPrice(inv.level_price)}`,
    note: inv.level_label,
  };
}
